import React, { useMemo } from 'react';

import { Icon } from 'components/tokens/Icon';
import { battery, drop, singleNeutral, tax } from 'icons/default';
import { Car, CarDashboardSpeed, GasLoad } from 'icons/symbol';
import { Colors } from 'enums/Colors';
import useMediaQuery from 'utils/useMediaQuery';
import { calculateMonthlyCost } from 'lib/loanCalculator/calculateMonthlyCostPrivateLoan';

import styles from './Highlights.module.scss';

interface IHighlights {
  headerItems: {
    monthlyCost: number;
    carInfoValuation: string;
    price: number;
  };
  items: {
    vehicleMalus: string | null;
    vehicleTax: string;
    numberOfOwners: string;
    combined: string;
    modelYear: number;
    miles: string[];
    typeOfFuel: string;
    range: string | null;
  };
}

const FALLBACK_STRING = '-';

export const Highlights: React.VFC<IHighlights> = ({ headerItems, items }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  let loanText: {
    term: string;
    details: string;
  };

  if (headerItems.monthlyCost) {
    loanText = {
      term: 'Fordonslån',
      details: `Fr. ${headerItems.monthlyCost} kr/mån`,
    };
  } else if (false && headerItems.price > 25000 && headerItems.price < 400000) {
    loanText = {
      term: 'Privatlån',
      details: `Fr. ${calculateMonthlyCost(headerItems.price)} kr/mån`,
    };
  } else {
    loanText = {
      term: 'Fordonslån',
      details: FALLBACK_STRING,
    };
  }

  const headerItemsWithTerm = useMemo(
    () => [
      loanText,
      {
        term: 'Pris',
        details: headerItems.carInfoValuation ?? FALLBACK_STRING,
      },
    ],
    [headerItems.carInfoValuation, headerItems.monthlyCost]
  );

  const itemsWithTermAndIcon = useMemo(
    () => [
      items.vehicleMalus
        ? {
            term: 'Skatt (år 1-3)',
            details: `${items.vehicleMalus} kr`,
            icon: tax,
          }
        : {
            term: 'Årsskatt',
            details: items.vehicleTax ? `${items.vehicleTax} kr` : FALLBACK_STRING,
            icon: tax,
          },
      {
        term: 'Ägare',
        details: items.numberOfOwners ?? FALLBACK_STRING,
        icon: singleNeutral,
        center: true,
      },
      items.typeOfFuel === 'Elbil'
        ? {
            term: 'Räckvidd',
            details: items.range ?? FALLBACK_STRING,
            icon: battery,
          }
        : {
            term: 'Förbrukning',
            details: items.combined ?? FALLBACK_STRING,
            icon: drop,
          },
      {
        term: 'Modellår',
        details: items.modelYear ?? FALLBACK_STRING,
        icon: Car,
      },
      {
        term: 'Miltal',
        details: items.miles ?? FALLBACK_STRING,
        icon: CarDashboardSpeed,
        center: true,
        bottom: true,
      },
      {
        term: 'Bränsle',
        details: items.typeOfFuel ?? FALLBACK_STRING,
        icon: GasLoad,
      },
    ],
    [
      items.combined,
      items.miles,
      items.modelYear,
      items.numberOfOwners,
      items.range,
      items.typeOfFuel,
      items.vehicleMalus,
      items.vehicleTax,
    ]
  );

  return (
    <dl className={styles['dl-wrapper']}>
      <div className={styles['dl-header-wrapper']}>
        {headerItemsWithTerm.map((item) => (
          <div className={styles['header-item-inner-wrapper']} key={item.term}>
            <dt className={styles.dt}>{item.term}</dt>
            <dd className={`${styles.dd} ${styles['header-dd']}`}>{item.details}</dd>
          </div>
        ))}
      </div>
      <div className={styles['items-wrapper']}>
        {itemsWithTermAndIcon.map((item, index) => (
          <React.Fragment key={item.term}>
            {isMobile && index === 3 ? <hr className={styles.divider} /> : null}
            <div
              className={`${styles['inner-items-wrapper']} ${
                item.center ? styles['inner-items-wrapper--center'] : ''
              } ${item.bottom ? styles['inner-items-wrapper--bottom'] : ''}`}
            >
              <Icon icon={item.icon} color={Colors.White} size={isMobile ? 16 : 24} />
              <dt className={styles.dt}>{item.term}</dt>
              <dd className={styles.dd}>{item.details}</dd>
            </div>
          </React.Fragment>
        ))}
      </div>
    </dl>
  );
};
